import { useEffect, useMemo, useState } from 'react'
import {
  searchSessionDetailToOpportunityResults,
  searchSessionDetailToResults,
  useSearchSession,
  useSearchSessions,
  type SearchSessionDetail,
  type SearchSessionDirection,
  type SearchSessionSummary,
} from '@/hooks/use-search-sessions'
import { useAutoLatestSearchSession } from '@/hooks/use-auto-latest-search-session'
import type { SearchCandidatesResponse } from '@/hooks/use-briefs'
import type {
  OpportunityMatch,
  OpportunitySearchResponse,
  UnifiedCandidate,
} from '@/hooks/use-smart-search'
import type { SearchProgressStage } from '@/components/recruiter/search-progress'

export type SmartAnalysisWorkbenchContext = {
  briefId?: string | null
  opportunityId?: string | null
  profileId?: string | null
  standalone?: boolean
}

export type SmartAnalysisWorkbenchLiveCandidates = {
  direction: 'candidates'
  results: SearchCandidatesResponse | null
  liveItems: UnifiedCandidate[]
  liveSessionId?: string | null
}

export type SmartAnalysisWorkbenchLiveOpportunities = {
  direction: 'opportunities'
  results: OpportunitySearchResponse | null
  liveItems: OpportunityMatch[]
  liveSessionId?: string | null
}

export type UseSmartAnalysisWorkbenchOptions = {
  direction: SearchSessionDirection
  context: SmartAnalysisWorkbenchContext
  enabled?: boolean
  live: SmartAnalysisWorkbenchLiveCandidates | SmartAnalysisWorkbenchLiveOpportunities
  stages: SearchProgressStage[]
  isSearching: boolean
  searchError: Error | null
  /** Reset selection when this scope key changes (e.g. opportunity id). */
  scopeKey?: string | null
}

type WorkbenchBase = {
  displayStages: SearchProgressStage[]
  displaySummary: string | null
  isHydrating: boolean
  isSearching: boolean
  searchError: Error | null
  prepareRun: () => void
  selectedSessionId: string | null
  setSelectedSessionId: (id: string | null) => void
  sessionSummaries: SearchSessionSummary[]
  selectedSessionDetail: SearchSessionDetail | undefined
  sessionsLoading: boolean
  selectedSessionLoading: boolean
}

type CandidatesWorkbench = WorkbenchBase & {
  displayResults: SearchCandidatesResponse | null
  displayLiveItems: UnifiedCandidate[]
}

type OpportunitiesWorkbench = WorkbenchBase & {
  displayResults: OpportunitySearchResponse | null
  displayLiveItems: OpportunityMatch[]
}

export function useSmartAnalysisWorkbench(
  options: UseSmartAnalysisWorkbenchOptions & {
    direction: 'candidates'
    live: SmartAnalysisWorkbenchLiveCandidates
  },
): CandidatesWorkbench

export function useSmartAnalysisWorkbench(
  options: UseSmartAnalysisWorkbenchOptions & {
    direction: 'opportunities'
    live: SmartAnalysisWorkbenchLiveOpportunities
  },
): OpportunitiesWorkbench

export function useSmartAnalysisWorkbench(
  options: UseSmartAnalysisWorkbenchOptions,
): CandidatesWorkbench | OpportunitiesWorkbench {
  const {
    direction,
    context,
    enabled = true,
    live,
    stages,
    isSearching,
    searchError,
    scopeKey,
  } = options

  const { briefId, opportunityId, profileId, standalone = false } = context
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const [stickySummary, setStickySummary] = useState<string | null>(null)
  const [stickyCandidates, setStickyCandidates] = useState<{
    results: SearchCandidatesResponse
    liveItems: UnifiedCandidate[]
  } | null>(null)
  const [stickyOpportunities, setStickyOpportunities] = useState<{
    results: OpportunitySearchResponse
    liveItems: OpportunityMatch[]
  } | null>(null)

  const sessionsEnabled =
    enabled &&
    (direction === 'opportunities'
      ? Boolean(profileId)
      : Boolean(briefId) || standalone || Boolean(opportunityId))

  const {
    data: sessionSummaries = [],
    isLoading: sessionsLoading,
  } = useSearchSessions({
    direction,
    briefId: briefId ?? null,
    standalone,
    opportunityId: opportunityId ?? null,
    profileId: profileId ?? null,
    enabled: sessionsEnabled,
  })

  const { data: selectedSessionDetail, isLoading: selectedSessionLoading } =
    useSearchSession(selectedSessionId)

  useAutoLatestSearchSession({
    sessions: sessionSummaries,
    sessionsLoading,
    isSearching,
    liveSessionId: live.results?.session_id,
    setSelectedSessionId,
  })

  useEffect(() => {
    setStickySummary(null)
    setStickyCandidates(null)
    setStickyOpportunities(null)
  }, [scopeKey])

  const hydratedResults = useMemo(() => {
    if (!selectedSessionDetail || isSearching) return null
    return direction === 'candidates'
      ? searchSessionDetailToResults(selectedSessionDetail)
      : searchSessionDetailToOpportunityResults(selectedSessionDetail)
  }, [selectedSessionDetail, isSearching, direction])

  const freshResults = useMemo(
    () => hydratedResults || live.results,
    [hydratedResults, live.results],
  )

  useEffect(() => {
    if (isSearching) return

    const results = freshResults
    if (!results?.session_id || (results.top_matches?.length ?? 0) === 0) return

    if (direction === 'candidates') {
      setStickyCandidates((prev) =>
        prev?.results.session_id === results.session_id
          ? prev
          : {
              results: results as SearchCandidatesResponse,
              liveItems: live.liveItems as UnifiedCandidate[],
            },
      )
      return
    }

    setStickyOpportunities((prev) =>
      prev?.results.session_id === results.session_id
        ? prev
        : {
            results: results as OpportunitySearchResponse,
            liveItems: live.liveItems as OpportunityMatch[],
          },
    )
  }, [direction, freshResults, isSearching, live.liveItems])

  const displayResults = useMemo(() => {
    if (isSearching) {
      if (direction === 'candidates' && stickyCandidates) {
        return stickyCandidates.results
      }
      if (direction === 'opportunities' && stickyOpportunities) {
        return stickyOpportunities.results
      }
    }
    return freshResults
  }, [isSearching, direction, stickyCandidates, stickyOpportunities, freshResults])

  useEffect(() => {
    const summary =
      direction === 'candidates'
        ? (displayResults as SearchCandidatesResponse | null)?.search_summary?.trim()
        : (displayResults as OpportunitySearchResponse | null)?.search_summary?.trim()
    if (summary) {
      setStickySummary((prev) => (prev === summary ? prev : summary))
    }
  }, [direction, displayResults])

  const displaySummary =
    (direction === 'candidates'
      ? (displayResults as SearchCandidatesResponse | null)?.search_summary?.trim()
      : (displayResults as OpportunitySearchResponse | null)?.search_summary?.trim()) ||
    stickySummary

  const isHydrating =
    sessionsLoading ||
    (Boolean(selectedSessionId) && selectedSessionLoading && !isSearching)

  const displayLiveItems = useMemo(() => {
    if (isSearching) {
      if (direction === 'candidates' && stickyCandidates) {
        return stickyCandidates.liveItems
      }
      if (direction === 'opportunities' && stickyOpportunities) {
        return stickyOpportunities.liveItems
      }
    }
    return selectedSessionId && !isSearching ? [] : live.liveItems
  }, [
    isSearching,
    direction,
    stickyCandidates,
    stickyOpportunities,
    selectedSessionId,
    live.liveItems,
  ])
  const displayStages = selectedSessionId && !isSearching ? [] : stages

  const prepareRun = () => {
    setSelectedSessionId(null)
  }

  const base = {
    displayStages,
    displaySummary,
    isHydrating,
    isSearching,
    searchError,
    prepareRun,
    selectedSessionId,
    setSelectedSessionId,
    sessionSummaries,
    selectedSessionDetail,
    sessionsLoading,
    selectedSessionLoading,
  }

  if (direction === 'candidates') {
    return {
      ...base,
      displayResults: displayResults as SearchCandidatesResponse | null,
      displayLiveItems: displayLiveItems as UnifiedCandidate[],
    }
  }

  return {
    ...base,
    displayResults: displayResults as OpportunitySearchResponse | null,
    displayLiveItems: displayLiveItems as OpportunityMatch[],
  }
}
